import { Folder, Loader } from 'lucide-react'
import { use } from 'react'
import { Link } from 'react-router'
import { CompanySuspensionContext } from '@/components/company-suspension/context'
import { H2, Muted } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from '@/components/ui/card'
import { TEMPLATE_CATEGORIES } from '@/config/templates'
import { useCompanyBilling, useCompanyPath, useRBAC } from '@/lib/hooks'

type Props = {
  id: string
  name: string
  displayName: string
  description: string | undefined | null
  logoUrl?: string | undefined | null
  categorySlug: string
  hasValidPaymentMethod?: boolean
  isDeploying?: boolean
  onClick?: (idTemplate: string) => void
}

export const TemplateCard = ({
  id,
  name,
  displayName,
  description,
  logoUrl,
  categorySlug,
  isDeploying,
}: Props) => {
  const { getCompanyPath, navigateToPath } = useCompanyPath()
  const { totalServices } = useRBAC()
  const { hasValidBankcard, remainingCredits } = useCompanyBilling()
  const { isSuspended } = use(CompanySuspensionContext)

  const isDeployEnabled =
    categorySlug === 'demo'
      ? remainingCredits > 0 && totalServices?.totalApps === 0
      : hasValidBankcard

  const isDisabled = !isDeployEnabled || isDeploying || isSuspended

  return (
    <Link
      to={getCompanyPath({
        pathname: '/template/:name',
        variables: { name },
      })}
    >
      <Card
        key={id}
        className="p-2 gap-2 transition-shadow hover:shadow-lg cursor-pointer"
      >
        <CardHeader className="pb-0 px-2 pt-2">
          <div className="flex items-center gap-2">
            <img
              src={logoUrl ?? ''}
              alt={displayName}
              className="max-h-[24px] w-auto"
            />
            <H2 className="text-md font-sans font-semibold">{displayName}</H2>
          </div>
        </CardHeader>
        <CardDescription className="px-2">
          {description || 'No description available.'}
        </CardDescription>
        <CardContent className="mt-auto px-2 py-2">
          <div className="flex items-center gap-1.5">
            <Folder className="size-4" />
            <Muted>
              {categorySlug && categorySlug in TEMPLATE_CATEGORIES
                ? TEMPLATE_CATEGORIES[
                    categorySlug as keyof typeof TEMPLATE_CATEGORIES
                  ]
                : ''}
            </Muted>
          </div>
        </CardContent>
        <CardFooter className="px-2 py-2">
          <Button
            variant="outline"
            disabled={isDisabled}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              navigateToPath({
                pathname: '/templates/:name/deploy',
                variables: { name },
                options: { replace: true },
                preserveQuery: true,
              })
            }}
          >
            {isDeploying ? (
              <>
                <Loader className="animate-spin" />
                <span>Deploying...</span>
              </>
            ) : (
              'Deploy'
            )}
          </Button>
        </CardFooter>
      </Card>
    </Link>
  )
}
